"use client"

import { useCallback, useEffect, useState } from "react"
import type { ThemeColors } from "./theme.types"
import { lightTheme } from "./light-theme"
import { darkTheme } from "./dark-theme"
import { grayTheme } from "./gray-theme"

export type ThemeMode = "system" | "light" | "dark" | "gray"

const THEME_MODE_COOKIE = "mode"
const THEME_MODE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365
const THEME_CHANGE_EVENT = "rudrise-theme-change"

const VALID_MODES: readonly ThemeMode[] = ["system", "light", "dark", "gray"]

export function setThemeModeCookie(mode: ThemeMode): void {
  if (typeof document === "undefined") return
  document.cookie = `${THEME_MODE_COOKIE}=${mode}; path=/; max-age=${THEME_MODE_COOKIE_MAX_AGE}`
}

export function getThemeModeCookie(): ThemeMode | null {
  if (typeof document === "undefined") return null
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${THEME_MODE_COOKIE}=`))
  if (!match) return null

  const value = match.slice(THEME_MODE_COOKIE.length + 1)
  return (VALID_MODES as readonly string[]).includes(value) ? (value as ThemeMode) : null
}

/** Resolves "system" against OS preferences if in browser, otherwise checks explicit mode */
export function resolveActualMode(mode: ThemeMode | null): "light" | "dark" | "gray" {
  if (mode === "dark") return "dark"
  if (mode === "gray") return "gray"
  if (mode === "system" && typeof window !== "undefined") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  }
  return "light"
}

export function resolveTheme(mode: ThemeMode | null): ThemeColors {
  const actual = resolveActualMode(mode)
  if (actual === "dark") return darkTheme
  if (actual === "gray") return grayTheme
  return lightTheme
}

export function getCurrentTheme(): ThemeColors {
  return resolveTheme(getThemeModeCookie())
}

/** Applies classes & data-theme attributes directly to the document root element */
export function applyThemeToDOM(mode: ThemeMode): void {
  if (typeof document === "undefined") return
  const actual = resolveActualMode(mode)

  document.documentElement.setAttribute("data-theme", mode)
  if (actual === "dark" || actual === "gray") {
    document.documentElement.classList.add("dark")
  } else {
    document.documentElement.classList.remove("dark")
  }
}

/** Tracks active theme mode and syncs across all hook consumers across the app */
export function useThemeMode() {
  const [mode, setModeState] = useState<ThemeMode>("light")

  useEffect(() => {
    // 1. Initial read from cookie
    const cookieMode = getThemeModeCookie() ?? "system"
    setModeState(cookieMode)
    applyThemeToDOM(cookieMode)

    // 2. Listen for theme updates triggered from other components
    const handleThemeChange = (e: Event) => {
      const customEvent = e as CustomEvent<ThemeMode>
      const nextMode = customEvent.detail ?? getThemeModeCookie() ?? "system"
      setModeState(nextMode)
      applyThemeToDOM(nextMode)
    }

    // 3. Listen for OS theme switches when in "system" mode
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    const handleMediaChange = () => {
      if ((getThemeModeCookie() ?? "system") === "system") {
        setModeState("system")
        applyThemeToDOM("system")
      }
    }

    window.addEventListener(THEME_CHANGE_EVENT, handleThemeChange)
    mediaQuery.addEventListener("change", handleMediaChange)

    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, handleThemeChange)
      mediaQuery.removeEventListener("change", handleMediaChange)
    }
  }, [])

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next)
    setThemeModeCookie(next)
    applyThemeToDOM(next)

    // Broadcast to AppTopBar and any other active useThemeMode instances
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: next }))
    }
  }, [])

  return { mode, theme: resolveTheme(mode), setMode }
}